import { useEffect } from "react";
import { useGetAllDhakaCityQuery } from "../../utils/dhakaCity.utils";
import {
  dhakaCityParseNamePostCode,
  useGetAllDhakaCityThanaQuery
} from "../../utils/dhakaCityThana.utils";
import { RenderFields, optionGen } from "../../utils/form.utils";
import { useGetAllDivisionQuery } from "../../utils/division.utils";
import { useGetAllDistrictQuery } from "../../utils/district.utils";
import { useGetAllUpazilaQuery } from "../../utils/upazila.utils";
import { useGetAllUnionQuery } from "../../utils/union.utils";
import {
  postOfficeParseNamePostCode,
  useGetAllPostOfficeQuery
} from "../../utils/postOffice.utils";
import { useGetAllVillageQuery } from "../../utils/village.utils";

export default function CustomerAddressField({ watch, control, errors, setValue }) {
  const thana = watch("address.thana");
  const division = watch("address.division");
  const district = watch("address.district");
  const upazila = watch("address.upazila");
  const union = watch("address.union");
  const isDhakaCity = watch("address.is_dhaka_city");

  const thanaId = thana?.value ?? "";
  const divisionId = division?.value ?? "";
  const districtId = district?.value ?? "";
  const upazilaId = upazila?.value ?? "";
  const unionId = union?.value ?? "";

  useEffect(() => {
    if (!thana) {
      setValue("address.area", "");
    }
  }, [thana, setValue]);

  const { loading: dhakaCityThanaLoading, data: dhakaCityThanaData } = useGetAllDhakaCityThanaQuery(
    {
      query: ``,
      key: `CustomerDhakaCityThanaEditFieldType`
    }
  );

  const { loading: divisionLoading, data: divisionData } = useGetAllDivisionQuery({
    query: ``,
    key: `CustomerDivisionEditFieldType`
  });

  const { isLoading: districtLoading, data: districtData } = useGetAllDistrictQuery({
    query: `?division_id=${divisionId}`,
    options: {
      enabled: !!divisionId
    },
    key: "CustomerDistrictEditFieldType"
  });

  const { isLoading: upazilaLoading, data: upazilaData } = useGetAllUpazilaQuery({
    query: `?district_id=${districtId}`,
    options: {
      enabled: !!districtId
    },
    key: "CustomerUpazilaEditFieldType"
  });

  const { isLoading: unionLoading, data: unionData } = useGetAllUnionQuery({
    query: `?upazila_id=${upazilaId}`,
    options: {
      enabled: !!upazilaId
    },
    key: "CustomerUnionEditFieldType"
  });

  const { isLoading: postOfficeLoading, data: postOfficeData } = useGetAllPostOfficeQuery({
    query: `?upazila_id=${upazilaId}`,
    options: {
      enabled: !!upazilaId
    },
    key: "CustomerPostOfficeEditFieldType"
  });

  const { isLoading: villageLoading, data: villageData } = useGetAllVillageQuery({
    query: `?union_id=${unionId}`,
    options: {
      enabled: !!unionId
    },
    key: "CustomerUnionEditFieldType"
  });

  const { isLoading: loading, data } = useGetAllDhakaCityQuery({
    query: `?dhaka_city_thana_id=${thanaId}`,
    options: {
      enabled: !!thanaId
    },
    key: "CustomerDhakaCityEditFieldType"
  });

  function getOptionData(optionData) {
    return optionData?.length ? optionGen(optionData) : [];
  }

  const dhakaCityThanaOptions = dhakaCityThanaData?.length
    ? optionGen(dhakaCityParseNamePostCode(dhakaCityThanaData), "name_post_code", "id")
    : [];

  const postOfficeOptions = postOfficeData?.length
    ? optionGen(postOfficeParseNamePostCode(postOfficeData), "name_post_code", "id")
    : [];

  const dhakaCityOptions = getOptionData(data);
  const divisionOptions = getOptionData(divisionData);
  const districtOptions = getOptionData(districtData);
  const upazilaOptions = getOptionData(upazilaData);
  const unionOptions = getOptionData(unionData);
  const villageOptions = getOptionData(villageData);

  const commonFields = [
    {
      label: "Dhaka City",
      name: "address.is_dhaka_city",
      type: "radio",
      col: { md: 6 },
      options: [
        {
          value: "1",
          label: "Yes"
        },
        {
          value: "2",
          label: "No"
        }
      ]
    },
    {
      label: "Address Type",
      name: "address.address_type",
      type: "radio",
      col: { md: 6 },
      options: [
        {
          value: "1",
          label: "Office"
        },
        {
          value: "2",
          label: "Home"
        }
      ]
    }
  ];

  const dhakaCityFields = [
    {
      label: "Thana",
      name: "address.thana",
      type: "select",
      placeholder: "Select a thana",
      col: { md: 6 },
      options: dhakaCityThanaOptions
    },
    {
      label: "Area",
      name: "address.area",
      type: "select",
      placeholder: "Select an area",
      col: { md: 6 },
      options: dhakaCityOptions
    },
    {
      label: "Street Address",
      name: "address.street",
      type: "textarea",
      placeholder: "Enter a street address",
      col: { md: 12 }
    }
  ];

  const outsideDhakaCityFields = [
    {
      label: "Division",
      name: "address.division",
      type: "select",
      placeholder: "Select a division",
      col: { md: 6 },
      options: divisionOptions
    },
    {
      label: "District",
      name: "address.district",
      type: "select",
      placeholder: "Select a district",
      col: { md: 6 },
      options: districtOptions
    },
    {
      label: "Upazila",
      name: "address.upazila",
      type: "select",
      placeholder: "Select a upazila",
      col: { md: 6 },
      options: upazilaOptions
    },
    {
      label: "Union",
      name: "address.union",
      type: "select",
      placeholder: "Select a union",
      col: { md: 6 },
      options: unionOptions
    },
    {
      label: "Village",
      name: "address.village",
      type: "select",
      placeholder: "Select a village",
      col: { md: 6 },
      options: villageOptions
    },
    {
      label: "Post Office",
      name: "address.post_office",
      type: "select",
      placeholder: "Select a post_office",
      col: { md: 6 },
      options: postOfficeOptions
    },
    {
      label: "Street Address",
      name: "address.street",
      type: "textarea",
      placeholder: "Enter a street address",
      col: { md: 12 }
    }
  ];

  const fields = [
    ...commonFields,
    ...(isDhakaCity === "1" ? dhakaCityFields : isDhakaCity === "2" ? outsideDhakaCityFields : [])
  ];

  const isLoading =
    loading ||
    dhakaCityThanaLoading ||
    divisionLoading ||
    districtLoading ||
    upazilaLoading ||
    unionLoading ||
    postOfficeLoading ||
    villageLoading;

  return (
    <>
      <p>Address</p>
      {isLoading && "Loading..."}
      {<RenderFields fields={fields} control={control} watch={watch} errors={errors} />}
    </>
  );
}
